// Copyright (C) 2023 Cochise Ruhulessin
// 
// All rights reserved. No warranty, explicit or implicit, provided. In
// no event shall the author(s) be liable for any claim or damages.
// 
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
import { useEffect, useState } from "react"
import {
  IonBackdrop,
  IonButton,
  IonCol,
  IonGrid,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonRow,
  IonText
} from "@ionic/react"
import { FadingIonList } from "../lib"
import { CurrentUser, IExpectedProduct, ITransactionDevice, Transaction } from "../services"

interface ContainerProps {
  nav: HTMLIonNavElement
  user: CurrentUser
  transaction: Transaction
}

const TransactionDevicesContainer: React.FC<ContainerProps> = ({
  nav,
  user,
  transaction
}) => {
  const [error, setError] = useState<string>("")
  const [imei, setImei] = useState<string>("")
  const [loading, setLoading] = useState<boolean>(false)
  const [receivedDevices, setReceivedDevices] = useState<ITransactionDevice[]>(transaction.getReceivedProducts())
  const [listedImeis, setListedDeviceIds] = useState<string[]>([])
  const [totalReceived, setTotalReceived] = useState<number>(transaction.getTotalReceived())
  const [expected, setExpected] = useState<IExpectedProduct[]>(transaction.getProducts())
  const classes: string[] = ["no-padding"]
  const hasUnknownDevices = !receivedDevices.every((device) => !!device.product_id)
  const hasUnexpectedDevices = !receivedDevices.every((device) => !!device.expected)
  const canClose = receivedDevices.length > 0
    && receivedDevices.every((device) => !!device.product_id)
    && receivedDevices.filter((device) => device.expected).length === transaction.getTotalExpected()
    && !hasUnexpectedDevices
    && !transaction.isClosed()

  const registerDevice = async (imei: string) => {
    if (listedImeis.includes(imei)) {
      return
    }
    try {
      const {device, expected} = await transaction.register(imei)
      setExpected(expected)
      if (device === null) {
        setError("Dit apparaat wordt niet herkend.")
        return
      }
      receivedDevices.push(device)
    } catch (e: any) {
      if (e.code !== "DEVICE_RECEIVED") { throw e }
      setError("Dit apparaat mag niet worden toegevoegd.")
    }
  }

  useEffect(() => {
    setListedDeviceIds(receivedDevices.map((device) => {return device.imei[0]}))
  }, [receivedDevices])

  useEffect(() => {
    setTotalReceived(receivedDevices.length)
  }, [receivedDevices])

  useEffect(() => {
    setImei("")
  }, [listedImeis])

  useEffect(() => setImei(""), [loading])

  if (!!error) classes.push("ion-invalid")
  return (
    <div className="container">
      {(!loading) ? null : (<IonBackdrop/>)}
      <hr/>
      <div style={{textAlign: "left", width: "100%"}}>
        <IonGrid className="fullscreen">
          <IonRow className="jumbotron">
            <IonCol>
              <h3>Apparaat registeren</h3>
              <p></p>
              <IonList lines="inset" mode="ios">
                <IonItem  className={classes.join(' ')}>
                  <IonLabel position="stacked" mode="md">IMEI</IonLabel>
                  <IonInput
                    autofocus={true}
                    clearInput={true}
                    enterkeyhint="search"
                    onKeyUp={(event) => {
                      setError("")
                      if (!!loading) { return }
                      if (event.key !== "Enter") { return }
                      if (imei.length < 14 || imei.length > 15) {
                        setError("Dit is geen geldige IMEI.")
                        return
                      }
                      setLoading(true)
                      registerDevice(imei).finally(() => setLoading(false))
                    }}
                    onIonChange={(event) => {
                      if (!event.detail || !event.detail.value) { return }
                      setImei(event.detail.value)
                    }}
                    placeholder="Gebruik de barcodescanner of vul handmatig een IMEI in"
                    value={imei}
                    readonly={loading}
                  />
                  <IonNote
                    slot="helper"
                    style={{textAlign: "right", width: "100%"}}
                  >
                    Let op: het apparaat wordt gelijk aan deze transactie toegevoegd!
                  </IonNote>
                  <IonNote
                    slot="error"
                    style={{textAlign: "right", width: "100%"}}
                  >
                    {error}
                  </IonNote>
                </IonItem>
              </IonList>
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol size="6">
              <IonButton
                disabled={!canClose}
                fill="clear"
                onClick={() => {
                  setLoading(true)
                  transaction.close()
                    .finally(() => setLoading(false))
                }}
                style={{display: (!!canClose) ? "inherit" : "none"}}
              >
                Ontvangst registreren
              </IonButton>
              <IonButton
                color="danger"
                disabled={!hasUnknownDevices}
                style={{display: (!!hasUnknownDevices) ? "inherit" : "none"}}
                fill="clear"
              >
                Onbekende apparaten corrigeren
              </IonButton>
            </IonCol>
            <IonCol size="6">
              <IonButton
                disabled={!transaction.isClosed()}
                fill="clear"
                style={{visibility: "hidden"}}
              >
                Ik heb de apparaten naar het magazijn gebracht
              </IonButton>
            </IonCol>
          </IonRow>
          <IonRow className="devices">
            <IonCol className="expected">
              <h3>Verwachte apparaten</h3>
              <div style={{maxHeight: "75%", minHeight: "75%", overflowY: "scroll"}} className="fade-y">
                <table style={{fontSize: "12px"}}>
                  <thead>
                    <tr>
                      <th>Naam</th>
                      <th>Grade</th>
                      <th>Aantal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expected.map((product, i) => {
                      return (
                        <tr key={i}>
                          <td>{product.displayName}</td>
                          <td>{product.grade}</td>
                          <td>{product.received}/{product.quantity}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </IonCol>
            <IonCol size="8" style={{marginLeft: "22px"}}>
              <h3>Ontvangen apparaten ({totalReceived})</h3>
              <IonText color="danger">
                <p style={{lineHeight: "22px"}}>
                  Let op: wijzig in Picqer
                  <b> niet</b> de aantallen voor producten
                  die ook op de onderstaande lijst zijn te vinden.
                </p>
              </IonText>
              <IonList lines="inset" style={{maxHeight: "75%", minHeight: "75%", overflowY: "scroll"}}>
                <FadingIonList show={5} height={77}>
                {receivedDevices.slice(0).reverse().map((device) => {
                  let label = `${device.name}, ${device.grade}, ${device.battery}%`
                  if (!!device.sku) {
                    label = `${device.sku}: ${label}`
                  }
                  return (
                    <IonItem
                      key={device.imei[0]}
                      className={`input-readonly ${device.expected === false && 'ion-invalid'}`}
                      style={{marginBottom: "11px"}}
                    >
                      <IonLabel position="floating">
                          <span style={{color: "var(--ion-color-primary)"}}> {device.registrar} </span>
                          op {device.registered.toLocaleDateString()}
                      </IonLabel>
                      <IonInput
                        className="input-readonly"
                        disabled={true}
                        style={{height: "44px"}}
                        readonly={true}
                        value={label}
                      />
                      <IonNote color="success" slot="helper">Apparaat geregistreerd.</IonNote>
                      <IonNote slot="error">Dit apparaat wordt niet verwacht. Andere grading?</IonNote>
                    </IonItem>
                  )
                })}
                </FadingIonList>
              </IonList>
            </IonCol>
          </IonRow>
        </IonGrid>
      </div>
    </div>
  )
}

export {TransactionDevicesContainer}
